import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  uploadConnectorService,
  BatchHistoryItem,
  BatchHistoryResponse,
  BatchHistoryFilters,
} from '../services/uploadConnector';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  Clock,
  Images,
  Zap,
  ChevronRight,
  Loader2,
  AlertCircle,
  History,
} from 'lucide-react';

/**
 * Human-readable transformation type names
 */
function getTransformationLabel(type: string): string {
  const labels: Record<string, string> = {
    background_removal: 'Background Removal',
    upscale: 'AI Upscale',
    generative_fill: 'Generative Fill',
    generative_remove: 'Generative Remove',
    generative_replace: 'Generative Replace',
    invisible_mannequin: 'Invisible Mannequin',
    virtual_staging: 'Virtual Staging',
    resize: 'Resize',
    crop: 'Crop',
    enhance: 'Enhance',
    studio_retouch: 'Studio Retouch',
  };
  return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Status badge styling
 */
function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'processing':
      return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    case 'completed':
      return 'bg-green-500/10 text-green-500 border-green-500/20';
    case 'partially_completed':
      return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    case 'failed':
      return 'bg-red-500/10 text-red-500 border-red-500/20';
    case 'pending':
    case 'uploading':
    default:
      return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'processing':
      return 'Processing';
    case 'completed':
      return 'Completed';
    case 'partially_completed':
      return 'Partially Completed';
    case 'failed':
      return 'Failed';
    case 'pending':
      return 'Pending';
    case 'uploading':
      return 'Uploading';
    default:
      return status;
  }
}

export function BatchHistoryPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State
  const [batches, setBatches] = useState<BatchHistoryItem[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState<BatchHistoryFilters>({
    page: 1,
    limit: 20,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Refs for polling and avoiding stale closures
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousStatusesRef = useRef<Map<string, string>>(new Map());
  const fetchRef = useRef<((newFilters?: BatchHistoryFilters) => Promise<void>) | undefined>(undefined);

  /**
   * Fetch batches from the API
   */
  const fetchBatches = useCallback(
    async (newFilters?: BatchHistoryFilters) => {
      const activeFilters = newFilters || filters;
      try {
        const response: BatchHistoryResponse =
          await uploadConnectorService.getBatchHistory(activeFilters);

        const newBatches = response.data;

        // Compare previous statuses with new ones to detect completions
        const prevStatuses = previousStatusesRef.current;
        for (const batch of newBatches) {
          const prevStatus = prevStatuses.get(batch.id);
          if (
            prevStatus === 'processing' &&
            (batch.status === 'completed' ||
              batch.status === 'partially_completed' ||
              batch.status === 'failed')
          ) {
            toast({
              title: `Batch "${batch.name}" completed`,
              description: `Status: ${getStatusLabel(batch.status)}`,
            });
          }
        }

        // Update previous statuses map
        const newStatusMap = new Map<string, string>();
        for (const batch of newBatches) {
          newStatusMap.set(batch.id, batch.status);
        }
        previousStatusesRef.current = newStatusMap;

        setBatches(newBatches);
        setPagination(response.pagination);
      } catch (err: any) {
        console.error('Failed to load batch history:', err);
        // Only show toast on initial load, not on silent polls
        if (isLoading) {
          toast({
            title: 'Error',
            description: 'Failed to load batch history',
            variant: 'destructive',
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [filters, toast, isLoading]
  );

  // Keep fetchRef up to date
  useEffect(() => {
    fetchRef.current = fetchBatches;
  }, [fetchBatches]);

  // Initial load
  useEffect(() => {
    fetchBatches();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch when filters change (after initial load)
  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    setIsLoading(true);
    fetchBatches(filters);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-polling: poll every 5 seconds if any batch is processing
  useEffect(() => {
    const hasProcessing = batches.some((b) => b.status === 'processing');

    if (hasProcessing) {
      pollIntervalRef.current = setInterval(() => {
        // Use fetchRef to avoid stale closures
        fetchRef.current?.();
      }, 5000);
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [batches]);

  // Filter handlers
  const handleStatusFilter = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      status: value === 'all' ? undefined : value,
      page: 1,
    }));
  };

  const handleDateFromFilter = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      dateFrom: value || undefined,
      page: 1,
    }));
  };

  const handleDateToFilter = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      dateTo: value || undefined,
      page: 1,
    }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: 20 });
  };

  const hasActiveFilters = !!(filters.status || filters.dateFrom || filters.dateTo);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (pagination.page > 1) {
      setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }));
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }));
    }
  };

  // Loading state
  if (isLoading && batches.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold">Batch History</h1>
            <p className="text-muted-foreground">
              Monitor all your asset transformation batches.
            </p>
          </div>
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
              <p className="mt-2 text-sm text-muted-foreground">Loading batch history...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold">Batch History</h1>
            <p className="text-muted-foreground mt-1">
              Monitor all your asset transformation batches.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/assets')}>
            <Images className="w-4 h-4 mr-2" />
            View Assets
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Status Filter */}
              <Select
                value={filters.status || 'all'}
                onValueChange={handleStatusFilter}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="partially_completed">Partially Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>

              {/* Date From */}
              <input
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => handleDateFromFilter(e.target.value)}
                placeholder="From"
                className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              />

              {/* Date To */}
              <input
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => handleDateToFilter(e.target.value)}
                placeholder="To"
                className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              />

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Batch List */}
        {batches.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <History className="h-12 w-12 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">No batch history</h2>
              <p className="text-muted-foreground">
                Processed batches will appear here
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {batches.map((batch) => {
              const progressValue =
                batch.total_images > 0
                  ? ((batch.processed_images + batch.failed_images) /
                      batch.total_images) *
                    100
                  : 0;

              return (
                <Card
                  key={batch.id}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate(`/batch-history/${batch.id}`)}
                >
                  <CardContent className="py-4">
                    <div className="space-y-3">
                      {/* Header: Name + Status */}
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate mr-4">
                          {batch.name}
                        </span>
                        <Badge
                          variant="outline"
                          className={`shrink-0 ${getStatusBadgeClass(batch.status)} ${
                            batch.status === 'processing' ? 'animate-pulse' : ''
                          }`}
                        >
                          {getStatusLabel(batch.status)}
                        </Badge>
                      </div>

                      {/* Progress bar for processing batches */}
                      {batch.status === 'processing' && (
                        <Progress value={progressValue} className="h-2" />
                      )}

                      {/* Stats row */}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Images className="h-4 w-4" />
                          {batch.processed_images}/{batch.total_images} processed
                        </span>
                        {batch.failed_images > 0 && (
                          <span className="flex items-center gap-1">
                            <AlertCircle className="h-4 w-4 text-red-500" />
                            {batch.failed_images} failed
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Zap className="h-4 w-4" />
                          {batch.transformation_credits} credits
                        </span>
                      </div>

                      {/* Transformations row */}
                      {batch.transformations && batch.transformations.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {batch.transformations.map((t, idx) => (
                            <Badge
                              key={idx}
                              variant="secondary"
                              className="text-xs"
                            >
                              {getTransformationLabel(t.type)}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Footer: Time + Chevron */}
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {formatDistanceToNow(new Date(batch.created_at), {
                            addSuffix: true,
                          })}
                        </span>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={pagination.page <= 1}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={pagination.page >= pagination.totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
